import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Storage as StorageIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

interface SystemStats {
  total_tenants: number;
  active_tenants: number;
  total_users: number;
  active_users: number;
  total_customers: number;
  total_interactions: number;
  total_documents: number;
  total_storage_mb: number;
  plan_distribution: Record<string, number>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

function Dashboard() {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // TODO: 実際のAPIコールに置き換える（認証トークン付き）
    // fetch('/api/admin/stats')
    //   .then(res => res.json())
    //   .then(data => setStats(data))
    //   .catch(err => setError(err.message))
    //   .finally(() => setLoading(false));

    // ダミーデータ
    setTimeout(() => {
      setStats({
        total_tenants: 5,
        active_tenants: 5,
        total_users: 25,
        active_users: 18,
        total_customers: 150,
        total_interactions: 450,
        total_documents: 75,
        total_storage_mb: 256.5,
        plan_distribution: {
          FREE: 2,
          BASIC: 2,
          PROFESSIONAL: 1,
        },
      });
      setLoading(false);
    }, 1000);
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        データの取得に失敗しました: {error}
      </Alert>
    );
  }

  if (!stats) return null;

  const planData = Object.entries(stats.plan_distribution).map(([name, value]) => ({
    name,
    value,
  }));

  const statCards = [
    {
      title: 'テナント数',
      value: stats.total_tenants,
      subValue: `アクティブ: ${stats.active_tenants}`,
      icon: <BusinessIcon />,
      color: '#1976d2',
    },
    {
      title: 'ユーザー数',
      value: stats.total_users,
      subValue: `アクティブ: ${stats.active_users}`,
      icon: <PeopleIcon />,
      color: '#388e3c',
    },
    {
      title: '法務チェック数',
      value: stats.total_documents,
      subValue: `容量: ${stats.total_storage_mb.toFixed(1)} MB`,
      icon: <DocumentIcon />,
      color: '#f57c00',
    },
    {
      title: '顧客数',
      value: stats.total_customers,
      subValue: `やりとり: ${stats.total_interactions}`,
      icon: <StorageIcon />,
      color: '#d32f2f',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        ダッシュボード
      </Typography>

      <Grid container spacing={3}>
        {/* 統計カード */}
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: `${stat.color}20`,
                      color: stat.color,
                      mr: 2,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" component="div">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {stat.subValue}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* プラン分布 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              プラン分布
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={planData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {planData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* 月次トレンド（仮） */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              月次トレンド
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { month: '1月', users: 15, tenants: 3 },
                  { month: '2月', users: 18, tenants: 4 },
                  { month: '3月', users: 20, tenants: 4 },
                  { month: '4月', users: 22, tenants: 5 },
                  { month: '5月', users: 24, tenants: 5 },
                  { month: '6月', users: 25, tenants: 5 },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="users" fill="#8884d8" name="ユーザー数" />
                <Bar dataKey="tenants" fill="#82ca9d" name="テナント数" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;